import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import { useBoundStore } from '../store';
import { type IAction, type ApiResponse, ApiVersions, type IHeaders, type IParams, RequestTypes } from './Api';

class HttpService {
  private readonly http: AxiosInstance;
  private readonly version: string;

  constructor(baseUrl: string, version: string) {
    this.version = version;
    this.http = axios.create({
      baseURL: baseUrl,
      withCredentials: false,
      headers: { 'Content-Type': 'application/json' },
    });
    this.injectInterceptors();
  }

  /**
   * Reads the token lazily from the bound store on every request —
   * mirrors the mobile pattern so the token is always fresh.
   * action / patientName drive the phi-* audit headers (same as mobile).
   */
  private setupHeaders({
    hasAttachment = false,
    action,
    patientName = '',
  }: {
    hasAttachment?: boolean;
    action?: IAction;
    patientName?: string;
  } = {}): Record<string, string | undefined> {
    const { accessToken } = useBoundStore.getState().shared;

    if (hasAttachment) {
      return { 'Content-Type': 'multipart/form-data' };
    }

    return {
      'Content-Type': 'application/json',
      Accept: `application/json;version=${this.version}`,
      'x-api-key': process.env.API_KEY ?? undefined,
      'action-name': action?.Name,
      'phi-action-type': action?.Type,
      'phi-action-description': action?.Description,
      'phi-patient-name': patientName || undefined,
      Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
    };
  }

  /** Strips header-only fields (action, patientName) from IParams before sending as URL query params. */
  private static toQueryParams(params?: IParams): Record<string, unknown> | undefined {
    if (!params) return undefined;
    const { action: _action, patientName: _patientName, ...rest } = params;
    void _action;
    void _patientName;
    return Object.keys(rest).length ? rest : undefined;
  }

  private injectInterceptors(): void {
    this.http.interceptors.request.use(
      (config) => config,
      (error) => Promise.reject(error),
    );
    this.http.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error) => Promise.reject(error),
    );
  }

  private async request<T>(
    method: RequestTypes,
    url: string,
    options: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.http.request<T>({
        method,
        url,
        ...options,
      });
      return {
        status: 'success',
        data: response.data,
        statusCode: response.status,
        timeStamp: response.headers['date'] as string,
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      const errorResponse: ApiResponse<T> = {
        status: 'error',
        message: axiosError.message,
        statusCode: axiosError.response?.status,
        timeStamp: new Date(),
        response: axiosError.response ?? {},
      };
      throw errorResponse;
    }
  }

  public async get<T>(
    url: string,
    params?: IParams,
    hasAttachment = false,
    headers?: IHeaders,
  ): Promise<ApiResponse<T>> {
    return this.request<T>(RequestTypes.GET, url, {
      params: HttpService.toQueryParams(params),
      headers: { ...headers, ...this.setupHeaders({ hasAttachment, action: params?.action, patientName: params?.patientName }) },
    });
  }

  public async post<T, P>(
    url: string,
    payload: P,
    params?: IParams,
    hasAttachment = false,
    headers?: IHeaders,
  ): Promise<ApiResponse<T>> {
    return this.request<T>(RequestTypes.POST, url, {
      params: HttpService.toQueryParams(params),
      data: payload,
      headers: { ...headers, ...this.setupHeaders({ hasAttachment, action: params?.action, patientName: params?.patientName }) },
    });
  }

  public async put<T, P>(
    url: string,
    payload: P,
    params?: IParams,
    hasAttachment = false,
    headers?: IHeaders,
  ): Promise<ApiResponse<T>> {
    return this.request<T>(RequestTypes.PUT, url, {
      params: HttpService.toQueryParams(params),
      data: payload,
      headers: { ...headers, ...this.setupHeaders({ hasAttachment, action: params?.action, patientName: params?.patientName }) },
    });
  }

  public async patch<T, P>(
    url: string,
    payload: P,
    params?: IParams,
    hasAttachment = false,
    headers?: IHeaders,
  ): Promise<ApiResponse<T>> {
    return this.request<T>(RequestTypes.PATCH, url, {
      params: HttpService.toQueryParams(params),
      data: payload,
      headers: { ...headers, ...this.setupHeaders({ hasAttachment, action: params?.action, patientName: params?.patientName }) },
    });
  }

  public async delete<T>(
    url: string,
    params?: IParams,
    hasAttachment = false,
    headers?: IHeaders,
  ): Promise<ApiResponse<T>> {
    return this.request<T>(RequestTypes.DELETE, url, {
      params: HttpService.toQueryParams(params),
      headers: { ...headers, ...this.setupHeaders({ hasAttachment, action: params?.action, patientName: params?.patientName }) },
    });
  }
}

export const communicationService = new HttpService(
  process.env.API_BASE_URL ?? '',
  ApiVersions.CHAT,
);
